import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRestaurantBranding } from '../services/api';

const BrandingContext = createContext();

// Default branding when restaurant has no custom config
const defaultBranding = {
  logoUrl: null,
  logoSmallUrl: null,
  faviconUrl: null,
  colors: {
    primary: '#7E4FB9',
    primaryHover: '#6b3fa3',
    primaryLight: 'rgba(126, 79, 185, 0.1)',
    secondary: '#A0F487',
    accent: '#8573AE',
  },
  fonts: {
    primary: 'DM Sans',
    secondary: 'Host Grotesk',
  },
  restaurantName: 'Menú Digital',
  tagline: null,
};

// Default settings for billing
const defaultSettings = {
  serviceCharge: 10,
  taxRate: 21,
  currency: 'ARS',
};

// Darken a hex color by a percentage (for hover states)
const darkenColor = (hex, percent = 10) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (num >> 16) - Math.round(2.55 * percent));
  const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(2.55 * percent));
  const b = Math.max(0, (num & 0x0000ff) - Math.round(2.55 * percent));
  return `#${(0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// Convert hex to rgba for light variant
const hexToRgba = (hex, alpha = 0.1) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Load Google Font dynamically
const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || fontFamily === 'DM Sans' || fontFamily === 'Host Grotesk' || fontFamily === 'DM Mono') return;

  const fontName = fontFamily.replace(/\s+/g, '+');
  const linkId = `google-font-${fontName}`;

  // Don't load if already loaded
  if (document.getElementById(linkId)) return;

  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
};

// Update favicon dynamically
const updateFavicon = (faviconUrl) => {
  if (!faviconUrl) return;

  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
};

// Known default names to detect "no custom restaurant name"
const DEFAULT_NAMES = ['Menú Digital', 'Digital Menu', 'Cardápio Digital'];

// Update document title with translated app name
const updateTitle = (restaurantName, appTitle) => {
  if (restaurantName && !DEFAULT_NAMES.includes(restaurantName)) {
    document.title = `${restaurantName} - ${appTitle}`;
  } else {
    document.title = appTitle;
  }
};

const defaultPaymentMethods = { cash: { enabled: true } };

// Canonical key for MercadoPago is camelCase `mercadoPago` (admin-panel emits it
// via normalizePaymentMethods). Tolerate legacy lowercase `mercadopago` payloads
// so the MP gate reads consistently across the app. See NER-1602.
const normalizePaymentMethods = (pm) => {
  if (!pm || typeof pm !== 'object') return pm;
  if (pm.mercadopago && !pm.mercadoPago) {
    const { mercadopago, ...rest } = pm;
    return { ...rest, mercadoPago: mercadopago };
  }
  return pm;
};

export function BrandingProvider({ children, restaurantId }) {
  const { t } = useTranslation();
  const [branding, setBranding] = useState(defaultBranding);
  const [settings, setSettings] = useState(defaultSettings);
  const [orderModes, setOrderModes] = useState({});
  const [restaurantLocation, setRestaurantLocation] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(defaultPaymentMethods);
  const [wifi, setWifi] = useState(null);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [loading, setLoading] = useState(true);

  // Apply branding to DOM (CSS variables, fonts, favicon)
  const applyBrandingToDOM = useCallback((brandingData) => {
    const root = document.documentElement;
    const colors = brandingData?.colors || {};

    // Apply color variables
    if (colors.primary) {
      root.style.setProperty('--color-primary', colors.primary);
      root.style.setProperty('--color-primary-hover', colors.primaryHover || darkenColor(colors.primary, 10));
      root.style.setProperty('--color-primary-light', colors.primaryLight || hexToRgba(colors.primary, 0.1));
    }

    if (colors.secondary) {
      root.style.setProperty('--color-secondary', colors.secondary);
    }

    if (colors.accent) {
      root.style.setProperty('--color-accent', colors.accent);
    }

    // Load and apply custom fonts
    const fonts = brandingData?.fonts || {};
    if (fonts.primary) {
      loadGoogleFont(fonts.primary);
      const fontStack = `'${fonts.primary}', -apple-system, BlinkMacSystemFont, sans-serif`;
      root.style.setProperty('--font-primary', fontStack);
      root.style.setProperty('--font-body', fontStack);
    }

    if (fonts.secondary) {
      loadGoogleFont(fonts.secondary);
      const fontStack = `'${fonts.secondary}', sans-serif`;
      root.style.setProperty('--font-secondary', fontStack);
      root.style.setProperty('--font-display', fontStack);
    }

    // Apply card/button style variables
    if (brandingData?.borderRadius) {
      root.style.setProperty('--card-border-radius', brandingData.borderRadius);
    }

    // Button border radius from buttonStyle
    const buttonStyle = brandingData?.buttonStyle || 'rounded';
    const btnRadiusMap = { pill: '50px', square: '4px', rounded: '12px' };
    root.style.setProperty('--btn-border-radius', btnRadiusMap[buttonStyle] || '12px');

    // Card style (shadow/border)
    const cardStyle = brandingData?.cardStyle || 'elevated';
    if (cardStyle === 'flat') {
      root.style.setProperty('--card-shadow', 'none');
      root.style.setProperty('--card-border', '1px solid var(--border-color, #E2DAF5)');
    } else if (cardStyle === 'outlined') {
      root.style.setProperty('--card-shadow', 'none');
      root.style.setProperty('--card-border', '2px solid var(--border-color, #E2DAF5)');
    } else {
      // elevated (default)
      root.style.setProperty('--card-shadow', 'var(--shadow-sm)');
      root.style.setProperty('--card-border', '1px solid var(--border-color, #E2DAF5)');
    }

    // Update favicon
    if (brandingData?.faviconUrl) {
      updateFavicon(brandingData.faviconUrl);
    }
  }, []);

  // Load branding from API
  const loadBranding = useCallback(async () => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }

    try {
      const data = await getRestaurantBranding(restaurantId);

      if (data) {
        // Merge with defaults
        const mergedBranding = {
          ...defaultBranding,
          ...data.branding,
          restaurantName: data.name || defaultBranding.restaurantName,
          colors: {
            ...defaultBranding.colors,
            ...(data.branding?.colors || {}),
          },
          fonts: {
            ...defaultBranding.fonts,
            ...(data.branding?.fonts || {}),
          },
        };

        setBranding(mergedBranding);
        applyBrandingToDOM(mergedBranding);
        updateTitle(mergedBranding.restaurantName, t('common.appTitle'));

        // Merge settings with defaults
        if (data.settings) {
          setSettings({
            ...defaultSettings,
            ...data.settings,
          });
        }

        // orderModes and restaurantLocation are TOP-LEVEL fields on the branding
        // payload (not nested under settings). Capture them so the mode picker and
        // delivery flow can read the restaurant's enabled modes / delivery radius.
        // Fallback to settings.* for forward-compat in case the contract changes. (NER-1319)
        setOrderModes(data.orderModes || data.settings?.orderModes || {});
        setRestaurantLocation(
          data.restaurantLocation || data.location || data.settings?.restaurantLocation || null
        );

        // Set payment methods
        if (data.paymentMethods) {
          setPaymentMethods(normalizePaymentMethods(data.paymentMethods));
        }

        // Set WiFi info
        if (data.wifi) {
          setWifi(data.wifi);
        }

        // Set feature flags from subscription plan
        if (data.featureFlags) {
          setFeatureFlags(data.featureFlags);
        }
      }
    } catch (error) {
      console.error('Error loading branding:', error);
      // Keep default branding on error
    } finally {
      setLoading(false);
    }
  }, [restaurantId, applyBrandingToDOM, t]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  // Update document title when language changes
  useEffect(() => {
    updateTitle(branding.restaurantName, t('common.appTitle'));
  }, [t, branding.restaurantName]);

  // Reset branding to defaults (useful when changing restaurants)
  const resetBranding = useCallback(() => {
    const root = document.documentElement;
    root.style.removeProperty('--color-primary');
    root.style.removeProperty('--color-primary-hover');
    root.style.removeProperty('--color-primary-light');
    root.style.removeProperty('--color-secondary');
    root.style.removeProperty('--color-accent');
    root.style.removeProperty('--font-primary');
    root.style.removeProperty('--font-secondary');
    root.style.removeProperty('--font-body');
    root.style.removeProperty('--font-display');
    root.style.removeProperty('--card-border-radius');
    root.style.removeProperty('--btn-border-radius');
    root.style.removeProperty('--card-shadow');
    root.style.removeProperty('--card-border');
    setBranding(defaultBranding);
    setSettings(defaultSettings);
    setOrderModes({});
    setRestaurantLocation(null);
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, settings, orderModes, restaurantLocation, paymentMethods, wifi, featureFlags, loading, resetBranding, darkModeEnabled: branding.darkModeEnabled !== false }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingContext;
